/**
 * DelayModule — DelayNode with a feedback loop and dry/wet mix.
 *
 * Graph:
 *
 *   input ─┬─ dryGain ────────────────────────► outputMix ─►
 *          └─► delay ─┬─► wetGain ─────────────► outputMix
 *                     └─► feedbackGain ─► delay (loop)
 *
 * Feedback is capped at 0.95 so a twisted patch doesn't run away — higher
 * values quickly swamp the dry signal and risk saturation beyond that.
 * Time is scheduled on the DelayNode's delayTime AudioParam, so changes
 * smooth naturally (pitch-shift artifacts appear if you swing it fast, which
 * is exactly the sound one usually wants).
 */

import type { AudioEngine } from '../AudioEngine';
import {
  EnumParameter,
  NumericParameter,
  type AnyParameter,
} from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';
import { SYNC_MODES, type SyncMode, type Transport } from '../Transport';
import { createPitchShifter, type PitchShifter } from '../pitchShifter';

const MAX_DELAY_SEC = 3;

export class DelayModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'delay' as const;
  readonly displayName = 'DELAY';
  readonly id: string;
  bypass = false;

  readonly syncMode = new EnumParameter<SyncMode>({
    name: 'Sync',
    default: 'free',
    options: SYNC_MODES,
    randomizable: false, // dice-rolling between free and 1/16T makes no sense
    formatter: (v) => (v === 'free' ? 'FREE' : v),
  });
  readonly time = new NumericParameter({
    name: 'Time',
    default: 0.375,
    min: 0.001,
    max: MAX_DELAY_SEC,
    curve: 'exp',
    unit: 's',
    smoothingMs: 30,
    formatter: (v) =>
      v < 1
        ? `${(v * 1000).toFixed(0)}ms`
        : `${v.toFixed(2)}s`,
  });
  readonly feedback = new NumericParameter({
    name: 'Feedback',
    default: 0.4,
    min: 0,
    max: 0.95,
    curve: 'linear',
    smoothingMs: 20,
    // Strong low bias — 0.9 feedback on a random patch produces a runaway
    // build-up that drowns the rest of the chain.
    randomSkew: 0.5,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 0.3,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    // Slight low bias — full wet delay is rarely what you want.
    randomSkew: 0.7,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  /**
   * Per-tap pitch shift, in semitones. Inserted *inside* the
   * feedback loop, so the shift compounds: first echo +N, second
   * +2N, third +3N, …. Set to 0 to bypass the shifter entirely
   * (no artifacts on a vanilla delay). Classic shimmer values:
   * +12 (octave-up cascade), +7 (cascading fifths), -12 (octave-
   * down sub-bass). Capped at ±24 to keep the granular shifter's
   * grain duration musical.
   */
  readonly pitch = new NumericParameter({
    name: 'Pitch',
    default: 0,
    min: -24,
    max: 24,
    curve: 'linear',
    smoothingMs: 0,
    randomSkew: 1,
    // Bias randomizer toward zero so a typical "Random" patch
    // doesn't always end up with extreme shimmer.
    formatter: (v) => {
      const n = Math.round(v);
      if (n === 0) return 'OFF';
      return `${n > 0 ? '+' : ''}${n} st`;
    },
  });

  readonly parameters: readonly AnyParameter[] = [
    this.syncMode,
    this.time,
    this.feedback,
    this.mix,
    this.pitch,
  ];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  private delayNode: DelayNode | null = null;
  private feedbackGain: GainNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  /** Granular pitch shifter inserted in the feedback loop. Each
   *  delay tap is shifted before being fed back, so the shift
   *  compounds on every repeat — the "shimmer" sound. Bypassed
   *  internally when pitch is 0. */
  private pitchShifter: PitchShifter | null = null;
  private started = false;
  private readonly transport: Transport | null;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string, transport?: Transport | null) {
    this.id = id ?? mintId('dly');
    this.transport = transport ?? null;
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    this.delayNode = ctx.createDelay(MAX_DELAY_SEC);
    this.delayNode.delayTime.value = this.computeDelaySeconds();
    this.feedbackGain = ctx.createGain();
    this.feedbackGain.gain.value = this.feedback.value;
    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();

    // Dry path
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputMixNode);

    // Wet path + feedback loop. Pitch shifter sits at the head of
    // BOTH the audible wet output and the feedback so every echo
    // is pitched, not just feedback. Routing the shifter only on
    // the feedback (a previous iteration) left the first echo at
    // the original pitch — sounded like a separate unpitched lane
    // playing alongside the shimmer cascade. With this wiring the
    // shifter sees every delay tap, and the loop still compounds
    // (each pass through the shifter adds another shift), giving
    // the classic +N / +2N / +3N shimmer climb.
    //
    //   input → delayNode → pitchShifter ─┬─ wetGain   ──► outputMix
    //                                     └─ feedbackGain ─┐
    //              ▲────────────────────────────────────────┘
    this.pitchShifter = createPitchShifter(ctx);
    this.inputNode.connect(this.delayNode);
    this.delayNode.connect(this.pitchShifter.input);
    this.pitchShifter.output.connect(this.feedbackGain);
    this.pitchShifter.output.connect(this.wetGain);
    this.feedbackGain.connect(this.delayNode);
    this.wetGain.connect(this.outputMixNode);

    this.feedback.bindAudioParam(this.feedbackGain.gain, ctx);
    this.applySyncBindings();

    this.applyMix(this.mix.value, true);
    // Seed the shifter with whatever pitch the preset / default
    // dictates, then keep it in sync with parameter changes.
    this.pitchShifter.setPitchSemitones(this.pitch.value);
    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));
    this.unsubs.push(this.syncMode.subscribe(() => this.applySyncBindings()));
    // subscribeDisplay (not subscribe) so audio-rate mod sources
    // routed to DLY · Pitch via the matrix actually drive the
    // shifter. The display channel fires on both UI value writes
    // AND `setModContribution()` updates from the mod router's
    // rAF sampler — `subscribe` only fires on `set()` calls, which
    // miss audio-rate modulation entirely (LFO/S&H/sequencer/
    // mod-wheel etc).
    this.unsubs.push(
      this.pitch.subscribeDisplay((v) => this.pitchShifter?.setPitchSemitones(v)),
    );
    if (this.transport) {
      this.unsubs.push(
        this.transport.bpm.subscribe(() => {
          if (this.syncMode.value !== 'free') this.writeSyncedDelayTime();
        }),
      );
    }

    this.started = true;
  }

  /** Look up an AudioParam for modulation routing. Used by ModRouter (global scope). */
  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Time') return this.delayNode?.delayTime ?? null;
    if (paramName === 'Feedback') return this.feedbackGain?.gain ?? null;
    return null;
  }

  /** Current intended delay time in seconds. */
  private computeDelaySeconds(): number {
    if (this.syncMode.value === 'free' || !this.transport) return this.time.value;
    return this.transport.divisionToSeconds(this.syncMode.value);
  }

  private applySyncBindings(): void {
    if (!this.delayNode || !this.engine) return;
    const free = this.syncMode.value === 'free' || !this.transport;
    if (free) {
      this.time.bindAudioParam(this.delayNode.delayTime, this.engine.context);
      this.time.set(this.time.value, 'init');
    } else {
      this.time.unbindAudioParam(this.delayNode.delayTime);
      this.writeSyncedDelayTime();
    }
  }

  private writeSyncedDelayTime(): void {
    if (!this.delayNode || !this.engine) return;
    const sec = this.computeDelaySeconds();
    const t = this.engine.context.currentTime;
    this.delayNode.delayTime.cancelScheduledValues(t);
    this.delayNode.delayTime.setTargetAtTime(sec, t, 0.03);
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.pitchShifter?.dispose();
    this.pitchShifter = null;
    this.inputNode?.disconnect();
    this.delayNode?.disconnect();
    this.feedbackGain?.disconnect();
    this.dryGain?.disconnect();
    this.wetGain?.disconnect();
    this.outputMixNode?.disconnect();
    this.inputNode = null;
    this.outputMixNode = null;
    this.delayNode = null;
    this.feedbackGain = null;
    this.dryGain = null;
    this.wetGain = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }

  private applyMix(mix: number, instant = false): void {
    if (!this.engine || !this.dryGain || !this.wetGain) return;
    const dry = Math.cos((mix * Math.PI) / 2);
    const wet = Math.sin((mix * Math.PI) / 2);
    const t = this.engine.context.currentTime;
    if (instant) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.02);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.02);
    }
  }
}
